import { Controller } from "@hotwired/stimulus";
import { Loader } from "@googlemaps/js-api-loader";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";

// Connects to data-controller="google-map"
export default class extends Controller {
  static values = {
    apiKey: String,
    refreshInterval: { type: Number, default: 0 },
    coordinates: { type: Array, default: [] },
    url: String,
    mapId: String,
    clustering: { type: Boolean, default: true },
    clusterMaxZoom: { type: Number, default: 11 },
    clusterRadius: { type: Number, default: 50 },
    clusterMinSize: { type: Number, default: 3 },
    heatmap: { type: Array, default: [] },
    gaps: { type: Array, default: [] },
    zoom: { type: Number, default: 6 },
    mode: { type: String, default: "heatmap" }
  };

  static targets = ["map"];

  static markerColors = {
    red: "#DB4437",
    blue: "#4285F4",
    green: "#0F9D58",
    yellow: "#F4B400",
    purple: "#9C27B0",
    pink: "#E91E63",
    orange: "#FF9800",
  };

  connect() {
    this.element.style.height = this.data.get("height") || "400px";
    this.markerElements = new Map();
    this.markers = [];
    this.infoWindows = [];

    // Google Maps doesn't need preloading as it's loaded via API key
    // The Loader handles dynamic loading of Google Maps libraries

    this.loader = new Loader({
      apiKey: this.apiKeyValue,
      version: "weekly",
      libraries: ["places", "marker", "visualization"],
    });

    this.initializeMap();
    this.setupAutoRefresh();

    // Listen for toggle
    const toggle = document.getElementById("toggle-map-mode");
    if (toggle) {
      toggle.addEventListener("change", (e) => {
        this.modeValue = e.target.checked ? "heatmap" : "markers";
        this.renderMapMode();
      });
    }
  }

  disconnect() {
    this.clearRefreshTimer();
    this.cleanupMarkers();
  }

  async initializeMap() {
    try {
      await this.loader.load();
      const { Map } = await google.maps.importLibrary("maps");
      const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker");

      this.Map = Map;
      this.AdvancedMarkerElement = AdvancedMarkerElement;
      this.PinElement = PinElement;

      const center = this.calculateCenter();
      const mapOptions = {
        center: center,
        zoom: this.zoomValue || 6,
        mapTypeId: google.maps.MapTypeId.ROADMAP
      };

      // Only set mapId if it's provided, otherwise use custom styles
      if (this.mapIdValue) {
        mapOptions.mapId = this.mapIdValue;
      } else {
        mapOptions.styles = [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          }
        ];
      }

      this.map = new this.Map(this.mapTarget, mapOptions);

      if (this.hasHeatmapValue) {
        this.initializeHeatmap();
      }

      if (this.hasGapsValue) {
        this.initializeGaps();
      }

      this.renderMapMode();
    } catch (error) {
      // Error initializing map
    }
  }

  async loadMarkers() {
    let coords = this.coordinatesValue;

    if (this.hasUrlValue) {
      try {
        const response = await fetch(this.urlValue);
        coords = await response.json();
          } catch (error) {
      // Failed to load coordinates
    }
  }

    this.cleanupMarkers();

    // Create all markers
    this.markers = await Promise.all(
      coords.map(coord => this.createAdvancedMarker(coord))
    );

    // Initialize marker clusterer if enabled
    if (this.clusteringValue && this.markers.length >= this.clusterMinSizeValue) {
      this.initializeMarkerClusterer();
    }

    if (this.markers.length > 0) {
      this.fitMapToMarkers();
    }

    // Also fit to all data for optimal view
    this.fitMapToAllData();
  }

  initializeMarkerClusterer() {
    // Clean up existing clusterer if it exists
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }

    // Create a new clusterer with the current markers
    this.markerClusterer = new MarkerClusterer({
      map: this.map,
      markers: this.markers,
      algorithm: new SuperClusterAlgorithm({
        maxZoom: this.clusterMaxZoomValue,
        radius: this.clusterRadiusValue
      }),
      renderer: {
        render: ({ count, position }) => {
          // Create a custom cluster marker
          const clusterDiv = document.createElement("div");
          clusterDiv.className = "cluster-marker";
          clusterDiv.innerHTML = `
            <div class="cluster-marker-inner">
              <span>${count}</span>
            </div>
          `;

          // Apply styles directly to avoid requiring external CSS
          clusterDiv.style.color = "white";
          clusterDiv.style.textAlign = "center";
          clusterDiv.style.fontSize = "14px";
          clusterDiv.style.fontWeight = "bold";

          const innerDiv = clusterDiv.querySelector(".cluster-marker-inner");
          const clusterSize = this.getClusterSize(count);

          innerDiv.style.backgroundColor = this.getClusterColor(count);
          innerDiv.style.borderRadius = "50%";
          innerDiv.style.width = `${clusterSize}px`;
          innerDiv.style.height = `${clusterSize}px`;
          innerDiv.style.display = "flex";
          innerDiv.style.alignItems = "center";
          innerDiv.style.justifyContent = "center";
          innerDiv.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";

          // Create the advanced marker for the cluster
          const clusterMarker = new this.AdvancedMarkerElement({
            position,
            content: clusterDiv,
            zIndex: 1000,
          });

          // Add click event to zoom in when cluster is clicked
          clusterMarker.addListener("gmp-click", () => {
            this.map.setCenter(position);
            this.map.setZoom(Math.min(
              this.map.getZoom() + 2,
              this.clusterMaxZoomValue + 1
            ));
          });

          return clusterMarker;
        }
      }
    });
  }

  async createAdvancedMarker(coord) {
    const pin = new this.PinElement({
      background: this.getMarkerColor(coord.color),
      borderColor: this.getMarkerColor(coord.borderColor) || "#ffffff",
      glyphColor: this.getMarkerColor(coord.glyphColor) || "#ffffff",
      scale: coord.scale || 1.0,
    });

    let content;
    if (coord.content) {
      content = this.createMarkerContent(coord.content);
    } else {
      pin.glyph = coord.glyph || (coord.label?.[0] || "");
      content = pin.element;
    }

    const marker = new this.AdvancedMarkerElement({
      position: { lat: coord.latitude, lng: coord.longitude },
      map: this.map,
      content,
      title: coord.title || "",
    });

    this.markerElements.set(marker, content);

    if (coord.info_content) {
      const infoWindow = new google.maps.InfoWindow({
        content: this.buildStyledInfoWindow(coord),
        ariaLabel: coord.title || "Marker info",
      });

      marker.addListener("gmp-click", () => {
        this.closeAllInfoWindows();
        infoWindow.open({
          anchor: marker,
          map: this.map,
        });
      });

      this.infoWindows = this.infoWindows || [];
      this.infoWindows.push(infoWindow);
    }

    return marker;
  }

  buildStyledInfoWindow(coord) {
    const statusColor = this.getStatusColor(coord.status);
    const gradeColor = this.getGradeColor(coord.grade_color);
    const accuracyBadge = this.getAccuracyBadge(coord.accuracy);
    const deviceIcon = coord.device_type === 'Mobile' ? '📱' : '💻';
    
    return `
      <div class="modern-info-window">
        <!-- Header Section -->
        <div class="info-header">
          <div class="staff-info">
            <div class="staff-details">
              <h3 class="staff-name">${coord.staff_name}</h3>
              <p class="staff-code">${coord.staff_code} • ${coord.staff_department || 'General'}</p>
            </div>
          </div>
          <div class="response-grade grade-${coord.grade_color}">
            ${coord.response_grade}
          </div>
        </div>

                 <!-- Activity Section -->
         <div class="activity-section">
           <div class="activity-item">
             <span class="activity-icon">🎯</span>
             <div class="activity-content">
               <p class="activity-title">${coord.activity || 'General Activity'}</p>
               <p class="activity-time">${coord.completed_at || coord.created_at}</p>
             </div>
           </div>
         </div>

        <!-- Location Section -->
        <div class="location-section">
          <div class="location-item">
            <span class="location-icon">📍</span>
            <div class="location-content">
              <p class="location-primary">${coord.address || `${coord.city}, ${coord.state}`}</p>
              <p class="location-secondary">${coord.staff_location || coord.city}</p>
            </div>
                     </div>
         </div>

                 <!-- Metadata Section -->
         <div class="metadata-section">
           <div class="metadata-item">
             <span class="metadata-icon">${deviceIcon}</span>
             <span class="metadata-text">${coord.device_type}</span>
           </div>
           ${coord.response_time ? `
           <div class="metadata-item">
             <span class="metadata-icon">⏱️</span>
             <span class="metadata-text">${coord.response_time}min</span>
           </div>
           ` : ''}
           ${accuracyBadge ? `
           <div class="metadata-item">
             <span class="metadata-icon">🎯</span>
             <span class="metadata-text">${coord.accuracy ? Math.round(coord.accuracy) + 'm' : 'N/A'}</span>
           </div>
           ` : ''}
         </div>

        <!-- Actions Section -->
        <div class="actions-section">
          <a href="${coord.map_url}" target="_blank" class="action-btn primary">
            <span class="action-icon">🗺️</span>
            View on Maps
          </a>
          <a href="${coord.tracking_url}" target="_blank" class="action-btn secondary">
            <span class="action-icon">📊</span>
            View Details
          </a>
        </div>
      </div>

      <style>
        .modern-info-window {
          width: 320px;
          font-family: 'Google Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: white;
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
          overflow: hidden;
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(8px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .info-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 16px 12px 16px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-bottom: 1px solid #e2e8f0;
        }

        .staff-info {
          display: flex;
          align-items: center;
          flex: 1;
        }

        .staff-details {
          flex: 1;
          min-width: 0;
        }

        .staff-name {
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
          line-height: 1.3;
          letter-spacing: -0.01em;
        }

        .staff-code {
          font-size: 12px;
          color: #64748b;
          margin: 1px 0 0 0;
          font-weight: 500;
          letter-spacing: 0.01em;
        }

        .response-grade {
          padding: 4px 10px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 700;
          min-width: 32px;
          text-align: center;
          flex-shrink: 0;
        }



        .activity-section {
          padding: 14px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #f1f5f9;
        }

        .activity-item {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1;
        }

        .activity-icon {
          font-size: 14px;
          opacity: 0.8;
        }

        .activity-content {
          flex: 1;
          min-width: 0;
        }

        .activity-title {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
          line-height: 1.3;
          letter-spacing: -0.01em;
        }

        .activity-time {
          font-size: 12px;
          color: #64748b;
          margin: 1px 0 0 0;
          font-weight: 500;
        }

        .grade-green { background: #dcfce7; color: #166534; }
        .grade-emerald { background: #d1fae5; color: #065f46; }
        .grade-purple { background: #f3e8ff; color: #6b21a8; }
        .grade-amber { background: #fef3c7; color: #92400e; }
        .grade-red { background: #fef2f2; color: #991b1b; }

        .location-section {
          padding: 14px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #f1f5f9;
        }

        .location-item {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1;
        }

        .location-icon {
          font-size: 14px;
          opacity: 0.8;
        }

        .location-content {
          flex: 1;
          min-width: 0;
        }

        .location-primary {
          font-size: 14px;
          font-weight: 500;
          color: #1e293b;
          margin: 0;
          line-height: 1.3;
          letter-spacing: -0.01em;
        }

        .location-secondary {
          font-size: 12px;
          color: #64748b;
          margin: 1px 0 0 0;
          font-weight: 500;
        }



        .metadata-section {
          padding: 10px 16px;
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .metadata-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #64748b;
        }

        .metadata-icon {
          font-size: 13px;
          opacity: 0.8;
        }

        .metadata-text {
          font-weight: 500;
        }

        .actions-section {
          padding: 14px 16px;
          display: flex;
          gap: 8px;
          background: #f8fafc;
        }

        .action-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px 14px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s ease;
          border: 1px solid transparent;
          letter-spacing: -0.01em;
        }

        .action-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        }

        .action-btn.primary {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
        }

        .action-btn.primary:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
        }

        .action-btn.secondary {
          background: white;
          color: #475569;
          border-color: #e2e8f0;
        }

        .action-btn.secondary:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #334155;
        }

        .action-icon {
          font-size: 14px;
          opacity: 0.9;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        /* Mobile Responsive */
        @media (max-width: 480px) {
          .modern-info-window {
            width: 290px;
          }
          
          .info-header {
            padding: 12px 14px 10px 14px;
          }
          
          .activity-section, .location-section {
            padding: 12px 14px;
          }
          
          .metadata-section {
            padding: 8px 14px;
          }
          
          .actions-section {
            flex-direction: column;
            padding: 12px 14px;
          }
          
          .action-btn {
            width: 100%;
          }
        }
      </style>
    `;
  }

  getStatusColor(status) {
    const colors = {
      'Completed': '#22c55e',
      'Overdue': '#ef4444',
      'Pending': '#f59e0b'
    };
    return colors[status] || '#64748b';
  }

  getGradeColor(gradeColor) {
    const colors = {
      'green': '#22c55e',
      'emerald': '#10b981',
      'purple': '#8b5cf6',
      'amber': '#f59e0b',
      'red': '#ef4444'
    };
    return colors[gradeColor] || '#64748b';
  }

  getAccuracyBadge(accuracy) {
    return accuracy ? true : false;
  }

  createMarkerContent(content) {
    const container = document.createElement("div");
    container.innerHTML = content;
    return container;
  }

  getMarkerColor(color) {
    if (!color) return undefined;
    return this.constructor.markerColors[color.toLowerCase()] || color;
  }

  cleanupMarkers() {
    // Clean up the marker clusterer if it exists
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
      this.markerClusterer = null;
    }

    // Clean up individual markers
    if (this.markers) {
      this.markers.forEach(marker => marker.map = null);
      this.markers = [];
    }

    // Clean up marker elements
    if (this.markerElements) {
      this.markerElements.forEach(element => {
        if (element.parentNode) element.parentNode.removeChild(element);
      });
      this.markerElements.clear();
    }

    this.closeAllInfoWindows();
  }

  closeAllInfoWindows() {
    if (this.infoWindows) {
      this.infoWindows.forEach(window => window.close());
    }
  }

  fitMapToMarkers() {
    const bounds = new google.maps.LatLngBounds();
    this.markers.forEach(marker => bounds.extend(marker.position));
    this.map.fitBounds(bounds);

    const maxZoom = parseInt(this.data.get("maxZoom")) || 15;
    const listener = google.maps.event.addListener(this.map, "bounds_changed", () => {
      if (this.map.getZoom() > maxZoom) {
        this.map.setZoom(maxZoom);
      }
      google.maps.event.removeListener(listener);
    });
  }

  fitMapToAllData() {
    const bounds = new google.maps.LatLngBounds();
    let hasData = false;

    // Add heatmap points to bounds
    if (this.hasHeatmapValue && this.heatmapValue.length > 0) {
      this.heatmapValue.forEach(point => {
        bounds.extend(new google.maps.LatLng(point.lat, point.lng));
        hasData = true;
      });
    }

    // Add coordinates to bounds
    if (this.coordinatesValue.length > 0) {
      this.coordinatesValue.forEach(coord => {
        const lat = coord.latitude || coord.lat;
        const lng = coord.longitude || coord.lng;
        if (lat && lng) {
          bounds.extend(new google.maps.LatLng(lat, lng));
          hasData = true;
        }
      });
    }

    // Add gaps to bounds
    if (this.hasGapsValue && this.gapsValue.length > 0) {
      this.gapsValue.forEach(gap => {
        bounds.extend(new google.maps.LatLng(gap.lat, gap.lng));
        hasData = true;
      });
    }

    // Fit bounds if we have data
    if (hasData && !bounds.isEmpty()) {
      this.map.fitBounds(bounds, 80);
    }
  }

  setupAutoRefresh() {
    if (this.refreshIntervalValue > 0) {
      this.refreshTimer = setInterval(() => {
        this.loadMarkers();
      }, this.refreshIntervalValue * 1000);
    }
  }

  clearRefreshTimer() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  getInitialCenter() {
    try {
      return JSON.parse(this.data.get("center")) || { lat: 0, lng: 0 };
    } catch {
      return { lat: 0, lng: 0 };
    }
  }

  getCustomMapOptions() {
    try {
      return JSON.parse(this.data.get("options")) || {};
    } catch {
      return {};
    }
  }

  getDefaultMapOptions() {
    return {
      zoomControl: true,
      mapTypeControl: true,
      scaleControl: true,
      streetViewControl: true,
      rotateControl: true,
      fullscreenControl: true,
      restriction: undefined,
      gestureHandling: "auto",
      ...this.getCustomMapOptions(),
    };
  }

  // Helper method to get cluster color based on marker count
  getClusterColor(count) {
    if (count < 10) return "#4285F4"; // Blue for small clusters
    if (count < 50) return "#0F9D58"; // Green for medium clusters
    if (count < 100) return "#F4B400"; // Yellow for large clusters
    return "#DB4437"; // Red for very large clusters
  }

  // Helper method to get cluster size based on marker count
  getClusterSize(count) {
    // Base size is 36px, grows logarithmically with count
    // Min size: 36px, Max size: 54px
    return Math.min(54, Math.max(36, 24 + Math.log10(count) * 12));
  }

  initializeHeatmap() {
    const heatmapData = this.heatmapValue.map(point => ({
      location: new google.maps.LatLng(point.lat, point.lng),
      weight: point.weight
    }));

    this.heatmap = new google.maps.visualization.HeatmapLayer({
      data: heatmapData,
      map: this.map,
      radius: 20,
      opacity: 0.8,
      gradient: [
        'rgba(0, 255, 255, 0)',
        'rgba(0, 255, 255, 1)',
        'rgba(0, 191, 255, 1)',
        'rgba(0, 127, 255, 1)',
        'rgba(0, 63, 255, 1)',
        'rgba(0, 0, 255, 1)',
        'rgba(0, 0, 223, 1)',
        'rgba(0, 0, 191, 1)',
        'rgba(0, 0, 159, 1)',
        'rgba(0, 0, 127, 1)',
        'rgba(63, 0, 91, 1)',
        'rgba(127, 0, 63, 1)',
        'rgba(191, 0, 31, 1)',
        'rgba(255, 0, 0, 1)'
      ]
    });
  }

  initializeGaps() {
    this.gapsValue.forEach(gap => {
      const marker = new google.maps.Marker({
        position: { lat: gap.lat, lng: gap.lng },
        map: this.map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: "#FCD34D",
          fillOpacity: 0.7,
          strokeColor: "#D97706",
          strokeWeight: 2
        },
        title: "Coverage Gap"
      });
    });
  }

  centerOnGap(event) {
    const lat = parseFloat(event.currentTarget.dataset.lat);
    const lng = parseFloat(event.currentTarget.dataset.lng);
    
    this.map.setCenter({ lat, lng });
    this.map.setZoom(15);
  }

  calculateCenter() {
    // Prioritize heatmap data for centering
    if (this.hasHeatmapValue && this.heatmapValue.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      this.heatmapValue.forEach(point => {
        bounds.extend(new google.maps.LatLng(point.lat, point.lng));
      });
      return bounds.getCenter();
    } 
    
    // Fall back to coordinates
    if (this.coordinatesValue.length > 0) {
      const validCoords = this.coordinatesValue.filter(coord => {
        const lat = coord.latitude || coord.lat;
        const lng = coord.longitude || coord.lng;
        return lat && lng;
      });
      
      if (validCoords.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        validCoords.forEach(coord => {
          bounds.extend(new google.maps.LatLng(coord.latitude || coord.lat, coord.longitude || coord.lng));
        });
        return bounds.getCenter();
      }
    }
    
    // Fall back to gaps
    if (this.hasGapsValue && this.gapsValue.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      this.gapsValue.forEach(gap => {
        bounds.extend(new google.maps.LatLng(gap.lat, gap.lng));
      });
      return bounds.getCenter();
    }
    
    // Default center (Ghana)
    return { lat: 5.5600, lng: -0.2057 };
  }

  renderMapMode() {
    // Clean up overlays
    if (this.heatmap) {
      this.heatmap.setMap(null);
      this.heatmap = null;
    }
    this.cleanupMarkers();
    if (this.modeValue === "heatmap" && this.hasHeatmapValue) {
      this.initializeHeatmap();
    } else if (this.hasCoordinatesValue) {
      this.loadMarkers();
    }
  }
}
